/**
 * Copyright © 2026 Adnuntius AS.
 */
import * as _ from 'lodash';

import {MoneyHelper, MoneyValue} from '../../components/util/money';
import {deriveGrossTargetCpm, Fee} from './fees-calculator';

export interface ProductCpmBounds {
  minimumCpm: MoneyValue;
  maximumCpm: MoneyValue;
}

export interface CreativeFormatFee {
  name: string;
  id: string;
  type: string;
  monetary: MoneyValue;
}

export type MaximumCost = MoneyValue | 0 | 'Unlimited';

export function deriveProductCpmBounds(
  fees: Fee[],
  product: {minimumCpm?: MoneyValue; maximumCpm?: MoneyValue} | null | undefined,
  primaryCurrency: string
): ProductCpmBounds {
  const emptyValue = (): MoneyValue => ({amount: 0, currency: primaryCurrency});
  const deriveBound = (value: MoneyValue | undefined): MoneyValue => {
    const derivedValue = value?.amount ? deriveGrossTargetCpm(fees, value) : undefined;
    return MoneyHelper.isMoney(derivedValue) ? derivedValue : emptyValue();
  };
  return {
    minimumCpm: deriveBound(product?.minimumCpm),
    maximumCpm: deriveBound(product?.maximumCpm)
  };
}

export function shouldApplyFixedProductBids(product: any, bidSpecification: any): boolean {
  return product?.cpmSpecification === 'FIXED' &&
    !MoneyHelper.isMoney(MoneyHelper.fixMoney(bidSpecification?.cpm)) &&
    !MoneyHelper.isMoney(MoneyHelper.fixMoney(bidSpecification?.cpc));
}

export function calculateMaximumCosts(
  editingModel: any,
  deliveryData: any,
  defaultCpmCostModel: string | null | undefined
): {maxCpmCost: MaximumCost; maxCpcCost: MaximumCost} {
  const costModel = editingModel?.bidSpecification?.cpmCostModel || defaultCpmCostModel || 'PER_IMPRESSION';
  const objectiveField = costModel === 'PER_IMPRESSION' ? 'IMPRESSION' : 'RENDERED_IMPRESSION';
  const deliveryField = costModel === 'PER_IMPRESSION' ? 'impressions' : 'renderedImpressions';
  const objectives = editingModel?.objectives || {};
  const bidSpecification = editingModel?.bidSpecification || {};

  let maxCpmCost: MaximumCost = 0;
  if (objectives[objectiveField] && bidSpecification.cpm?.amount) {
    maxCpmCost = _.cloneDeep(bidSpecification.cpm) as MoneyValue;
    maxCpmCost.amount *= Math.max(0, (objectives[objectiveField] - (deliveryData?.[deliveryField] || 0)) / 1000);
  }

  let maxCpcCost: MaximumCost = 0;
  if (objectives.CLICK && bidSpecification.cpc?.amount) {
    maxCpcCost = _.cloneDeep(bidSpecification.cpc) as MoneyValue;
    maxCpcCost.amount *= Math.max(0, objectives.CLICK - (deliveryData?.clicks || 0));
  }

  const hasDeliveryObjective = ['IMPRESSION', 'VIEWABLE_IMPRESSION', 'VISIBLE_IMPRESSION', 'CLICK', 'RENDERED_IMPRESSION']
    .some(objective => !!objectives[objective]);
  if (hasDeliveryObjective && maxCpmCost === 0 && bidSpecification.cpm?.amount) {
    maxCpmCost = 'Unlimited';
  }

  return {maxCpmCost, maxCpcCost};
}

export function summarizeCreativeFormatFees(
  formatFees: CreativeFormatFee[],
  numCreatives: number,
  referenceCurrency: string,
  name: string
): CreativeFormatFee | undefined {
  const applicableFees = formatFees.filter(fee => fee.monetary.currency === referenceCurrency);
  const uniqueFees = _.uniqBy(applicableFees, fee => fee.monetary.amount);
  if (uniqueFees.length === 0) {
    return undefined;
  }

  const sortedFees = _.sortBy(uniqueFees, ['monetary.amount']);
  const fee: CreativeFormatFee = {
    name,
    id: 'creative-format-fee',
    type: 'MONETARY',
    monetary: {
      currency: referenceCurrency,
      amount: sortedFees[0].monetary.amount
    }
  };
  if (uniqueFees.length === 1 && numCreatives === applicableFees.length) {
    return fee;
  }

  fee.monetary.amount = numCreatives > applicableFees.length ? 0 : sortedFees[0].monetary.amount;
  fee.monetary.maxAmount = sortedFees[sortedFees.length - 1].monetary.amount;
  return fee;
}
